import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  isRandomStaff,
  useResourceNotes,
} from '../../../../../helpers/merchants/resources/helper';
import { bookingEvent } from '../../../../../utils/gtag';

import { useShallowEqualSelector } from '../../../../../hooks/redux';
import { scrollToTop } from '../../../../../hooks/useScrollToTop';
import {
  CreateUserWithResvQuery,
  useAuth,
} from '../../../../../hooks/api/auth';
import { useCollectRwgConversion } from 'src/hooks/rwg';
import { SettingCompletion } from '../../../../../interfaces/merchants/resources/books';

import { ReduxState } from '../../../../../redux/reducers';
import { getMerchantResourcesBooksInputs } from '../../../../../redux/selectors/merchants/resources/books/inputs';
import { setMeToBookInfo } from '../../../../../redux/actions/merchants/resources/books';

import LabeledContainer from '../../../../../molecules/common/LabeledContainer';
import BooksMenuItemList from '../../../../../molecules/merchant/resources/book/BooksMenuItemList';
import BooksNotes from '../../../../../molecules/merchant/resources/book/BooksNotes';
import BooksSection from '../../../../../molecules/merchant/resources/book/BooksSection';
import BooksCompleteAlert from '../../../../../molecules/merchant/resources/book/BooksCompleteAlert';
import BooksRequestAlert from '../../../../../molecules/merchant/resources/book/BooksRequestAlert';
import BooksCenteringUnderLineMessage from '../../../../../molecules/merchant/resources/book/BooksCenteringUnderLineMessage';
import InputCompletionCreateUser from '../../../../../molecules/merchant/resources/book/InputCompletionCreateUser';
import { useAppDispatch } from '../../../../../redux';
import { ErrorType } from '../../../../../domain/entities/Auth';
import { RSButtonAnchor } from 'src/reservation-page/RSButtonAnchor';
import { buildSiteUrl } from 'src/utils/request-url';

interface ElementProps {
  merchantResource: {
    publicId: string;
    name: string;
    imageUrl?: string;
    modelType: string;
  };
  settings: SettingCompletion;
  publicId: string;
  withExpressFlow: boolean;
  resourcePublicId: string;
}

type Props = ElementProps;

const InputCompletion: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const { createUserWithResv } = useAuth();
  const [errors, setErrors] = useState<ErrorType[]>();
  const { merchantResource, settings, withExpressFlow, resourcePublicId } =
    props;
  const intl = useIntl();
  const notes = useResourceNotes(settings);

  const reservationPublicId = settings.createUserWithResv!.reservationId;

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [postable, setPostable] = useState(true);

  const useSelector = () =>
    useShallowEqualSelector((state: ReduxState) => ({
      inputs: getMerchantResourcesBooksInputs(
        state.merchantsResourcesBooksInputs,
      ),
    }));
  const { inputs } = useSelector();

  const continuousReservationQuery = useMemo(() => {
    const params = new URLSearchParams();
    params.set('continuous_reservation', 'true');
    if (inputs.course)
      params.set('selected_course', inputs.course.query_id.toString());
    if (inputs.staff)
      params.set('selected_staff', inputs.staff.query_id.toString());

    return params.toString();
  }, [inputs.course, inputs.staff]);

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    // Fire GTM booking_completion event
    bookingEvent({
      step: 'completion',
      reservationPublicId,
      resourcePublicId,
    });
  }, [reservationPublicId, resourcePublicId]);

  useCollectRwgConversion();

  const handleCreateUser = useCallback(
    async (query: CreateUserWithResvQuery) => {
      setPostable(false);
      createUserWithResv(query)
        .then(async ({ errorType }) => {
          if (errorType) return setErrors([errorType]);

          setSubmitted(true);
          await dispatch(setMeToBookInfo(inputs.merchantId!, resourcePublicId));
        })
        .catch((_err) => {
          return;
        })
        .finally(() => setPostable(true));
    },
    [createUserWithResv, dispatch, inputs.merchantId, resourcePublicId],
  );

  const reservations = inputs;
  const reservationId = settings.createUserWithResv!.reservationId;

  return (
    <div>
      {settings.finishedStatus === 'accepted' && <BooksCompleteAlert />}
      {settings.finishedStatus === 'pending' && <BooksRequestAlert />}
      <BooksSection>
        {settings.createUserWithResv!.available &&
          (submitted ? (
            <BooksCenteringUnderLineMessage>
              {intl.formatMessage({
                id: 'books.formsLabel.createUserSubmitted',
              })}
            </BooksCenteringUnderLineMessage>
          ) : (
            <InputCompletionCreateUser
              postable={postable}
              reservationId={reservationId}
              errors={errors}
              email={reservations.userInfo!.customerFields!.email}
              onSubmit={(query: CreateUserWithResvQuery) =>
                handleCreateUser(query)
              }
            />
          ))}
      </BooksSection>
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.booksItems',
        })}
        paddingTop={true}
      >
        {reservations.course && (
          <BooksMenuItemList
            nickname={props.publicId}
            resourceId={merchantResource.publicId}
            resourceName={merchantResource.name}
            resourceImage={merchantResource.imageUrl ?? ''}
            resourceModelType={merchantResource.modelType}
            resourceUrl={buildSiteUrl(`/${merchantResource.publicId}`)}
            options={reservations.options || []}
            name={reservations.course ? reservations.course.name : ''}
            price={
              reservations.payment &&
              reservations.payment.paymentMethod.method !== 'subscription' &&
              reservations.payment.paymentMethod.method !== 'ticket' &&
              reservations.payment.paymentMethod.method !==
                'first_time_free_voucher'
                ? reservations.payment.paymentMethod.price
                : undefined
            }
            payment={
              reservations.payment && reservations.payment.paymentMethod.method
            }
            staff={
              reservations.staff && !isRandomStaff(reservations.staff)
                ? reservations.staff.name
                : ''
            }
            courseDate={
              reservations.timeSlotAvailability
                ? reservations.timeSlotAvailability.date
                : ''
            }
            courseTime={
              reservations.timeSlotAvailability
                ? reservations.timeSlotAvailability.value
                : ''
            }
            googleCalendarTemplateUrl={
              reservations.googleCalendarTemplateUrl ?? ''
            }
          />
        )}
      </LabeledContainer>
      {!withExpressFlow ? (
        <>
          <BooksSection withPadding={true} rightSide={true}>
            <RSButtonAnchor variant="outline" isBlock href={buildSiteUrl()}>
              {intl.formatMessage({
                id: 'books.formsLabel.backToMainPage',
              })}
            </RSButtonAnchor>
          </BooksSection>
          <BooksSection withPadding={true} rightSide={true}>
            <RSButtonAnchor
              variant="outline"
              isBlock
              href={buildSiteUrl(
                `/${resourcePublicId}/book?${continuousReservationQuery}`,
              )}
            >
              {intl.formatMessage({
                id: 'books.formsLabel.continuousReservation',
              })}
            </RSButtonAnchor>
          </BooksSection>
        </>
      ) : (
        <BooksSection withPadding={true} rightSide={true}>
          <RSButtonAnchor
            variant="outline"
            isBlock
            href={buildSiteUrl(
              `/${resourcePublicId}/express?${continuousReservationQuery}`,
            )}
          >
            {intl.formatMessage({
              id: 'books.formsLabel.continuousReservation',
            })}
          </RSButtonAnchor>
        </BooksSection>
      )}
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.attentionForBooking',
        })}
        contentsPadding={true}
        paddingTop={true}
      >
        <BooksNotes notes={notes} />
      </LabeledContainer>
    </div>
  );
};

export default InputCompletion;
